// Thermal Section - Progressive 1D → 2D → 3D Navigation with 4 Experimental Conditions
import { useState, useEffect } from 'preact/hooks'
import { basis, domain } from '../../../state/globalSignals.js'
import ThermalDimensionView from '../plots/ThermalDimensionView.jsx'
import DimensionStepper from '../plots/DimensionStepper.jsx'
import { formatValue } from '../../../utils/formatUtils.js'
import { THERMAL_SECTION, renderDefinition } from '../../../data/definitions.js'

// Note: X-axis domain (Power ↔ Current) now uses global 'domain' signal from globalSignals.js

// Color scheme for 4 experimental conditions (matching Energy/Power sections)
const CONDITION_COLORS = {
  c10: '#ea5b0c',      // Orange - C/10
  dc_25: '#0077b6',    // Blue - Standard
  cont: '#d62828',     // Red - Continuous
  peak: '#48cae4'      // Cyan - Peak
}

// SVG Icons
const HeatIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v10"></path>
    <path d="M18.4 6.6a9 9 0 1 1-12.77.04"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
)

const TemperatureIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 14.76V3.5a2.5 2.5 0 0 0-5 0v11.26a4.5 4.5 0 1 0 5 0z"></path>
  </svg>
)

const EfficiencyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="m16 12-4-4-4 4"></path>
    <path d="M12 16V8"></path>
  </svg>
)

// Helper: Get 4 thermal values for each category (matching Energy/Power pattern)
function get4Values(cellData, categoryId, currentBasis) {
  if (!cellData?.thermal) return null
  
  const therm = cellData.thermal
  const mass_kg = cellData.mechanical?.mass_g ? cellData.mechanical.mass_g / 1000 : null
  const vol_L = cellData.mechanical?.vol_cell_cm3 ? cellData.mechanical.vol_cell_cm3 / 1000 : null
  
  switch(categoryId) {
    case 'heat-power':
      // Power loss [W] - Standard only
      let p_dc_25 = therm?.P_loss_dc_25 || 0;
      let unit_p = 'W';
      
      if (currentBasis === 'grav' && mass_kg) {
        p_dc_25 /= mass_kg;
        unit_p = 'W/kg';
      } else if (currentBasis === 'vol' && vol_L) {
        p_dc_25 /= vol_L;
        unit_p = 'W/L';
      }
      
      return {
        dc_25: { value: p_dc_25, unit: unit_p, label: 'Standard', color: CONDITION_COLORS.dc_25 }
      };
      
    case 'heat-energy':
      // Energy loss [Wh] - Standard only
      let w_dc_25 = therm?.W_loss_dc_25 || 0;
      let unit_w = 'Wh';
      
      if (currentBasis === 'grav' && mass_kg) {
        w_dc_25 /= mass_kg;
        unit_w = 'Wh/kg';
      } else if (currentBasis === 'vol' && vol_L) {
        w_dc_25 /= vol_L;
        unit_w = 'Wh/L';
      }
      
      return {
        dc_25: { value: w_dc_25, unit: unit_w, label: 'Standard', color: CONDITION_COLORS.dc_25 }
      };
      
    case 'temperature':
      // Max temperature [°C] - Standard only
      const t_dc_25 = therm?.T_max_dc_25 || 0;
      
      return {
        dc_25: { value: t_dc_25, unit: '°C', label: 'Standard', color: CONDITION_COLORS.dc_25 }
      };
      
    case 'efficiency':
      // Efficiency [%] - Standard only
      // Use electrical.eta_dc_pct if available, otherwise from thermal.eta_dc_25
      const elec_eta = cellData?.electrical?.eta_dc_pct || 0;
      const therm_eta = therm?.eta_dc_25 || 0;
      const eta_dc_25 = elec_eta || therm_eta;
      
      return {
        dc_25: { value: eta_dc_25, unit: '%', label: 'Standard', color: CONDITION_COLORS.dc_25 }
      };
      
    default:
      return null;
  }
}

const CATEGORIES = [
  { 
    id: 'heat-power', 
    label: 'Heat Power', 
    icon: <HeatIcon />
  },
  { 
    id: 'heat-energy', 
    label: 'Heat Energy', 
    icon: <HeatIcon />
  },
  { 
    id: 'temperature', 
    label: 'Max Temperature', 
    icon: <TemperatureIcon />
  },
  { 
    id: 'efficiency', 
    label: 'Efficiency', 
    icon: <EfficiencyIcon />
  }
]

export default function ThermalSection({ cellData }) {
  const [activeCategory, setActiveCategory] = useState('heat-power')
  const [dimension, setDimension] = useState(() => {
    return localStorage.getItem('batemo:thermal-dimension') || '2d'
  })
  // Flipcard state for each category
  const [flippedCards, setFlippedCards] = useState({})

  // Save dimension to localStorage
  useEffect(() => {
    localStorage.setItem('batemo:thermal-dimension', dimension)
  }, [dimension])

  const activeCat = CATEGORIES.find(c => c.id === activeCategory)
  
  // Toggle flip for current category
  const toggleFlip = () => {
    setFlippedCards(prev => ({
      ...prev,
      [activeCategory]: !prev[activeCategory]
    }))
  }
  
  // Get the definition for current category
  const getCategoryDefinition = () => {
    const defMap = {
      'heat-power': THERMAL_SECTION.heatPower,
      'heat-energy': THERMAL_SECTION.heatEnergy,
      'temperature': THERMAL_SECTION.temperature,
      'efficiency': THERMAL_SECTION.efficiency
    }
    const def = defMap[activeCategory] || { title: '', text: '' }
    // Use dynamic title if available, otherwise fallback to static title
    const dynamicTitle = def.getTitle ? def.getTitle(domain.value) : def.title
    return { ...def, title: dynamicTitle }
  }
  
  const currentDef = getCategoryDefinition()

  return (
    <div className="thermal-section-new">
      {/* Side Navigation Panel */}
      <aside className="thermal-nav" data-tour="thermal-nav">
        <div className="nav-header">
          <h3>Thermal Analysis</h3>
          <div className="nav-subtitle">4 Experimental Conditions</div>
        </div>
        
        <div className="category-list">
          {CATEGORIES.map(cat => {
            const values = get4Values(cellData, cat.id, basis.value)
            
            return (
              <button
                key={cat.id}
                className={`category-card ${activeCategory === cat.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                <div className="category-icon">{cat.icon}</div>
                <div className="category-info">
                  <div className="category-label">{cat.label}</div>
                  {values ? (
                    <div className="category-values-chips">
                      {Object.values(values).map((v, idx) => (
                        <div 
                          key={idx}
                          className="value-chip" 
                          style={`border-color: ${v.color}`}
                        >
                          <span className="value-chip-label" style={`color: ${v.color}`}>
                            {v.label}
                          </span>
                          <span className="value-chip-value" style={`color: ${v.color}`}>
                            {formatValue(v.value, v.unit)}
                            <span className="value-chip-unit" style={`color: ${v.color}`}>
                              {v.unit}
                            </span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="category-value-na">N/A</span>
                  )}
                </div>
                {activeCategory === cat.id && (
                  <div className="category-indicator"></div>
                )}
              </button>
            )
          })}
        </div>

      </aside>

      {/* Main Content Area */}
      <main className="thermal-content">
        {/* Dimension Stepper - Shared Component */}
        <DimensionStepper
          dimension={dimension}
          onDimensionChange={setDimension}
          data-tour="thermal-dimension-stepper"
        />

        {/* Content Area - Flipcard */}
        <div className={`thermal-flipcard flipcard ${flippedCards[activeCategory] ? 'flipped' : ''}`}>
          <div className="flipcard-inner">
            {/* Front: Chart/Plot */}
            <div className="flipcard-front thermal-flipcard-front">
              <button className="flip-btn" onClick={toggleFlip} title="Show info">?</button>
              <h3 className="column-title">{currentDef.title}</h3>
              
              <ThermalDimensionView 
                cellData={cellData}
                categoryName={activeCategory}
                dimension={dimension}
              />
            </div>
            
            {/* Back: Definition */}
            <div className="flipcard-back thermal-flipcard-back">
              <button className="flip-btn flip-btn-back" onClick={toggleFlip} title="Close">×</button>
              <h3 className="column-title">{currentDef.title}</h3>
              <div className="flip-content">
                <div dangerouslySetInnerHTML={{ __html: renderDefinition(currentDef.text, cellData) }} />
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
